<template>
  <bv-modal
    v-model="modelState"
    @cancel="handleDialogClose"
    @confirm="handleAddPresence"
    title="Set Your Presence"
  >
    <form @keydown.enter="handleAddPresence" class="modal__group">
      <div class="modal__control">
        <text-input disabled label="Presence" v-model="presenceName"></text-input>
      </div>
      <div class="modal__control">
        <text-input
          :error="v$.presenceDescription.$error"
          v-model="v$.presenceDescription.$model"
          label="Description"
          placeholder="Enter your presence description ..."
          required
          showCharacters
        >
          <template #errors>
            <p v-if="v$.presenceDescription.$errors.length">
              {{ v$.presenceDescription.$errors[0].$message }}
            </p>
          </template>
        </text-input>
      </div>
    </form>
  </bv-modal>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import useVuelidate from '@vuelidate/core'
import { required, maxLength, minLength, helpers } from '@/utilities/i18n-validators'
import { livePanelStore } from '@/stores/socket/live-panel-socket.store'
import socketHandler from '@/helpers/websocket/socketIO'
import TextInput from '@/components/SharedComponents/Settings/Inputs/TextInput.vue'
import BvModal from '@bv/components/modal/BvModal.vue'
import { trans } from '@bv/utils/i18n'

const emit = defineEmits(['closePresenceModal'])

const store = livePanelStore()
const { currentUserExtension } = storeToRefs(store)

const presenceName = ref('Other')
const presenceDescription = ref('')
const modelState = defineModel()

const rules = {
  presenceDescription: {
    required,
    minLength: minLength(5),
    maxLength: maxLength(50),
    noComma: helpers.withMessage(
      trans('validations.comma_not_allowed'),
      (value) => !/[,]+/.test(value)
    ),
    pausedIsNotallawed: helpers.withMessage(
      trans('account.set_presence_form.paused_is_not_allowed'),
      (value) => !/^paused$/.test(value.toLowerCase())
    )
  }
}

const v$ = useVuelidate(rules, { presenceDescription })

const handleDialogClose = () => {
  emit('closePresenceModal')
}

const handleAddPresence = () => {
  v$.value.$touch()
  if (!v$.value.$invalid) {
    socketHandler.sendAction('setMyPresence', {
      status: 'away',
      subType: 'other',
      message: presenceDescription.value
    })

    modelState.value = false
    v$.value.$reset()
  }
}

onMounted(() => {
  if (
    currentUserExtension.value?.presence?.status.toLowerCase() === 'away' &&
    currentUserExtension.value?.presence?.subType.toLowerCase() === 'other'
  ) {
    presenceDescription.value = currentUserExtension.value.presence.message
  }
})
</script>

<style lang="scss" scoped>
.modal {
  &__control {
    &:not(:last-child) {
      margin-block-end: 16px;
    }
  }
}
</style>
